<script setup lang="ts">
import { useRefObjectSync } from "@soft-db/soft-utils";
import { FormField, NumberInput, Switch } from "@soft-db/ui";
import PanelConfigurationSection from "./PanelConfigurationSection.vue";
import { ChartAxisOptions, ChartAxisRangeOptions } from "./type/chart-axes-options";

const { t } = useI18n();

defineProps<{
	name: string;
}>();

const vModel = defineModel<ChartAxisOptions>({ required: true });

const visibleField = useRefObjectSync(vModel, "visible");
const gridField = useRefObjectSync(vModel, "grid");
const labelField = useRefObjectSync(vModel, "label");
const rangeField = useRefObjectSync(vModel, "range");
const logScale = useRefObjectSync(vModel, "logScale");

function makeRangeField(name: keyof ChartAxisRangeOptions) {
	return computed({
		get() {
			return rangeField.value?.[name] ?? null;
		},
		set(val) {
			if (rangeField.value == null) {
				return;
			}

			rangeField.value = {
				...rangeField.value,
				[name]: val,
			};
		},
	});
}

const minField = makeRangeField("min");
const maxField = makeRangeField("max");
</script>

<template>
	<PanelConfigurationSection :name="name">
		<FormField v-if="visibleField != null" :label="t('dashboard.visible')" labelPosition="left">
			<Switch v-model="visibleField" color="primary" />
		</FormField>

		<FormField v-if="gridField != null" :label="t('dashboard.gridLine')" labelPosition="left">
			<Switch v-model="gridField" color="primary" />
		</FormField>

		<FormField
			v-if="logScale != null"
			:label="t('dashboard.logarithmicScale')"
			labelPosition="left"
		>
			<Switch v-model="logScale" color="primary" />
		</FormField>

		<SoftInput v-if="labelField != null" v-model="labelField" :label="$t('dashboard.label')" />

		<div v-if="rangeField" class="grid grid-cols-2 gap-2">
			<FormField :label="$t('dashboard.min')">
				<NumberInput
					v-model="minField"
					class="w-full"
					type="number"
					placeholder="auto"
					emptyAsNull
				/>
			</FormField>

			<FormField :label="$t('dashboard.max')">
				<NumberInput
					v-model="maxField"
					class="w-full"
					type="number"
					placeholder="auto"
					emptyAsNull
				/>
			</FormField>
		</div>
	</PanelConfigurationSection>
</template>
